import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Custom hook to block navigation when there are unsaved changes.
 * Works with BrowserRouter (legacy router) by using beforeunload for browser navigation
 * and intercepting React Router navigation via history API.
 *
 * @param {boolean} shouldBlock - Whether navigation should be blocked
 * @param {string} message - Optional custom message for confirmation dialog
 * @returns {Object} Object with showConfirm state, handlers, and navigation state
 */
const useNavigationBlocker = (shouldBlock, message) => {
  const location = useLocation();
  const shouldBlockRef = useRef(shouldBlock);
  const locationRef = useRef(location);
  const isNavigatingRef = useRef(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const pendingNavigationRef = useRef(null);
  const originalPushStateRef = useRef(null);
  const originalReplaceStateRef = useRef(null);

  // Keep refs up to date
  useEffect(() => {
    shouldBlockRef.current = shouldBlock;
  }, [shouldBlock]);

  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  // Function to execute pending navigation
  const executePendingNavigation = () => {
    if (pendingNavigationRef.current) {
      const { type, args } = pendingNavigationRef.current;
      isNavigatingRef.current = true;

      if (type === 'pushState' && originalPushStateRef.current) {
        originalPushStateRef.current.apply(window.history, args);
      } else if (type === 'replaceState' && originalReplaceStateRef.current) {
        originalReplaceStateRef.current.apply(window.history, args);
      } else if (type === 'popstate') {
        // For popstate, allow the navigation by not pushing state back
        // The browser already navigated, we just need to allow it
      } else if (type === 'link') {
        const href = args[0];
        if (href) {
          const pushState = originalPushStateRef.current || window.history.pushState;
          pushState.call(window.history, null, '', href);
          // Notify React Router of the navigation without a full page refresh
          window.dispatchEvent(new PopStateEvent('popstate'));
        }
      } else if (type === 'refresh') {
        // For refresh, actually reload the page
        window.location.reload();
        return; // Don't reset flags since page is reloading
      }

      setTimeout(() => {
        isNavigatingRef.current = false;
        pendingNavigationRef.current = null;
      }, 100);
    }
  };

  // Handle keyboard shortcuts for refresh (F5, Ctrl+R, Ctrl+Shift+R)
  useEffect(() => {
    if (!shouldBlock) {
      return;
    }

    const handleKeyDown = (e) => {
      // Check for refresh shortcuts: F5, Ctrl+R, Ctrl+Shift+R
      const isF5 = e.key === 'F5';
      const isCtrlR = (e.ctrlKey || e.metaKey) && e.key === 'r' && !e.shiftKey;
      const isCtrlShiftR = (e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'R';

      if (isF5 || isCtrlR || isCtrlShiftR) {
        if (shouldBlockRef.current && !isNavigatingRef.current) {
          e.preventDefault();
          e.stopPropagation();
          pendingNavigationRef.current = { type: 'refresh', args: [] };
          setShowConfirm(true);
        }
      }
    };

    // Use capture phase to intercept before other handlers
    window.addEventListener('keydown', handleKeyDown, true);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [shouldBlock]);

  // Handle browser navigation (refresh, close tab, etc.)
  // This is a fallback for when user clicks browser refresh button
  useEffect(() => {
    if (!shouldBlock) {
      return;
    }

    const handleBeforeUnload = (e) => {
      // Only show browser alert if we haven't already shown our custom modal
      // This happens when user clicks browser refresh button (not keyboard shortcut)
      if (!showConfirm) {
        e.preventDefault();
        e.returnValue = ''; // Required for Chrome
        return ''; // Required for Safari
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [shouldBlock, showConfirm]);

  // Intercept React Router navigation by detecting location changes
  useEffect(() => {
    if (!shouldBlock) {
      return;
    }

    const previousLocation = locationRef.current.pathname + locationRef.current.search;

    // Store original history methods
    if (!originalPushStateRef.current) {
      originalPushStateRef.current = window.history.pushState;
      originalReplaceStateRef.current = window.history.replaceState;
    }

    const originalPushState = originalPushStateRef.current;
    const originalReplaceState = originalReplaceStateRef.current;

    // Override pushState
    window.history.pushState = function (...args) {
      if (shouldBlockRef.current && !isNavigatingRef.current) {
        pendingNavigationRef.current = { type: 'pushState', args };
        setShowConfirm(true);
        return;
      }
      return originalPushState.apply(this, args);
    };

    // Override replaceState
    window.history.replaceState = function (...args) {
      if (shouldBlockRef.current && !isNavigatingRef.current) {
        pendingNavigationRef.current = { type: 'replaceState', args };
        setShowConfirm(true);
        return;
      }
      return originalReplaceState.apply(this, args);
    };

    // Handle popstate (back/forward buttons)
    const handlePopState = () => {
      if (shouldBlockRef.current && !isNavigatingRef.current) {
        // Push current state back to prevent navigation
        // Set flag before pushState to prevent recursive interception by overridden pushState
        isNavigatingRef.current = true;
        window.history.pushState(null, '', previousLocation);
        isNavigatingRef.current = false;
        pendingNavigationRef.current = { type: 'popstate', args: [] };
        setShowConfirm(true);
        return;
      }
    };

    // Intercept Link clicks (for React Router Link components)
    const handleLinkClick = (e) => {
      if (!shouldBlockRef.current || isNavigatingRef.current) {
        return;
      }

      const link = e.target.closest('a[href]');
      if (link) {
        const href = link.getAttribute('href');
        // Only intercept internal navigation (starts with /)
        if (href && href.startsWith('/') && !href.startsWith('//')) {
          const currentPath = locationRef.current.pathname + locationRef.current.search;

          // Only intercept if navigating to a different route
          if (href !== currentPath && href.split('?')[0] !== currentPath.split('?')[0]) {
            e.preventDefault();
            e.stopPropagation();
            // Store href instead of link element to avoid re-triggering click events
            pendingNavigationRef.current = { type: 'link', args: [href] };
            setShowConfirm(true);
          }
        }
      }
    };

    window.addEventListener('popstate', handlePopState);
    document.addEventListener('click', handleLinkClick, true);

    return () => {
      // Restore original methods
      if (originalPushStateRef.current) {
        window.history.pushState = originalPushStateRef.current;
        window.history.replaceState = originalReplaceStateRef.current;
      }
      window.removeEventListener('popstate', handlePopState);
      document.removeEventListener('click', handleLinkClick, true);
    };
  }, [shouldBlock, message]);

  const handleConfirm = () => {
    setShowConfirm(false);
    const pendingType = pendingNavigationRef.current?.type;

    // If it's a custom callback (modal close, etc.), execute it
    if (pendingType === 'custom') {
      const callback = pendingNavigationRef.current?.callback;
      if (callback) {
        callback();
      }
    } else {
      // Otherwise, execute navigation
      executePendingNavigation();
    }

    pendingNavigationRef.current = null;
  };

  const handleCancel = () => {
    const pendingType = pendingNavigationRef.current?.type;
    setShowConfirm(false);
    pendingNavigationRef.current = null;
    // For popstate, we need to push the current state back to prevent navigation
    if (pendingType === 'popstate') {
      // Set flag before pushState to prevent recursive interception by overridden pushState
      isNavigatingRef.current = true;
      window.history.pushState(null, '', locationRef.current.pathname + locationRef.current.search);
      isNavigatingRef.current = false;
    }
  };

  // Function to trigger confirmation for custom close attempts (modals, etc.)
  // Accepts isDirty parameter to check current state instead of relying on ref
  const triggerConfirm = (callback, isDirty = null) => {
    // Use provided isDirty if available, otherwise fall back to ref
    const shouldBlockNow = isDirty !== null ? isDirty : shouldBlockRef.current;

    if (shouldBlockNow && !isNavigatingRef.current) {
      pendingNavigationRef.current = { type: 'custom', callback };
      setShowConfirm(true);
    } else if (callback) {
      // If not blocking, execute callback immediately
      callback();
    }
  };

  return {
    showConfirm,
    handleConfirm,
    handleCancel,
    triggerConfirm,
    message
  };
};

export default useNavigationBlocker;
